// Import-from-disk pipeline. Single verb; no UI opinion.
//
// File → validate → decode → commit. The only path that gates before
// hitting storage. Every failure is returned as a structured result so
// the caller renders its own message; this module never touches the DOM.
//
// Why gate *before* decoding, and also *after*: mime+size keep us from
// wasting memory on a 200 MB mp4. But Web Audio decoders will happily
// accept a very-long-but-small file (e.g. a 10-minute podcast encoded
// as mono Opus ~5 MB). The duration check catches those after decode,
// which is the only place we can measure true length.

import { commit } from './commit';
import type { SampleStorage } from './storage/idb';
import type { Sample } from './types';

export const IMPORT_SIZE_LIMIT_BYTES = 8 * 1024 * 1024; // 8 MB
export const IMPORT_DURATION_LIMIT_S = 120; // 2 min

export type ImportResult =
  | { ok: true; sample: Sample }
  | { ok: false; reason: ImportRejectReason; detail?: string };

export type ImportRejectReason =
  | 'not-audio'
  | 'too-large'
  | 'too-long'
  | 'decode-failed'
  | 'empty-name';

export type DecodeResult =
  | { ok: true; buffer: AudioBuffer; suggestedName: string; filename: string }
  | { ok: false; reason: ImportRejectReason; detail?: string };

/** Validate + decode a file to an AudioBuffer without committing. Used
 *  by the forge's Import verb, which trims + commits on user action
 *  rather than immediately. Callers that want the commit-on-drop flow
 *  use importAudioFile below. */
export async function decodeAudioFile(
  file: File,
  ctx: AudioContext,
): Promise<DecodeResult> {
  if (!isAudioMime(file)) {
    return { ok: false, reason: 'not-audio', detail: file.type || '(no type)' };
  }
  if (file.size > IMPORT_SIZE_LIMIT_BYTES) {
    return {
      ok: false,
      reason: 'too-large',
      detail: formatBytes(file.size) + ' > ' + formatBytes(IMPORT_SIZE_LIMIT_BYTES),
    };
  }
  let buffer: AudioBuffer;
  try {
    buffer = await ctx.decodeAudioData(await file.arrayBuffer());
  } catch (e) {
    return {
      ok: false,
      reason: 'decode-failed',
      detail: e instanceof Error ? e.message : String(e),
    };
  }
  if (buffer.duration > IMPORT_DURATION_LIMIT_S) {
    return {
      ok: false,
      reason: 'too-long',
      detail: buffer.duration.toFixed(1) + 's > ' + IMPORT_DURATION_LIMIT_S + 's',
    };
  }
  const suggestedName = nameFromFilename(file.name);
  if (!suggestedName) {
    return { ok: false, reason: 'empty-name', detail: file.name };
  }
  return { ok: true, buffer, suggestedName, filename: file.name };
}

export async function importAudioFile(
  file: File,
  storage: SampleStorage,
  ctx: AudioContext,
): Promise<ImportResult> {
  const decoded = await decodeAudioFile(file, ctx);
  if (!decoded.ok) {
    return { ok: false, reason: decoded.reason, detail: decoded.detail };
  }
  try {
    const sample = await commit(
      {
        buffer: decoded.buffer,
        name: decoded.suggestedName,
        origin: { kind: 'import', filename: decoded.filename },
      },
      storage,
      ctx,
    );
    return { ok: true, sample };
  } catch (e) {
    return {
      ok: false,
      reason: 'decode-failed',
      detail: e instanceof Error ? e.message : String(e),
    };
  }
}

// Browsers are inconsistent about file.type for some formats (Opus/OGG
// can come through as empty string, some WAVs as 'audio/wave'). We
// primarily trust the mime, but fall back to extension for the "file
// browser stripped the type" case. Reject only when we have neither.
function isAudioMime(file: File): boolean {
  if (file.type && file.type.startsWith('audio/')) return true;
  const ext = file.name.toLowerCase().split('.').pop();
  if (!ext) return false;
  return AUDIO_EXTENSIONS.has(ext);
}

const AUDIO_EXTENSIONS = new Set([
  'wav',
  'wave',
  'mp3',
  'ogg',
  'oga',
  'opus',
  'flac',
  'm4a',
  'aac',
  'aiff',
  'aif',
  'webm',
]);

// "Foo Kick.wav" → "foo_kick". Mirrors commit.sanitizeName but runs on
// the stem (we don't want the .wav in the sample name).
export function nameFromFilename(filename: string): string {
  const stem = filename.replace(/\.[^.]+$/, '');
  return stem
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

function formatBytes(n: number): string {
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  return (n / (1024 * 1024)).toFixed(1) + ' MB';
}
